import type { DataSource } from "@estelle/domain";

import { createApiDataSource } from "@/adapters/api";

/**
 * The data source the application runs on when nobody passes one.
 *
 * The real adapter when an API is configured, fixtures otherwise.
 * `VITE_API_URL` absent means a checkout with no backend still boots and
 * still demonstrates every screen — which is how the whole F lane was built
 * before an API existed, and what keeps it reviewable now.
 *
 * The fixtures arrive through a dynamic `import()` for one reason: Vite
 * replaces `import.meta.env.VITE_API_URL` with the configured string at build
 * time, the ternary below then has a constant test, and the branch holding
 * the `import()` is dropped — together with the fixture adapter and every
 * row of sample data behind it. A build with `VITE_API_URL` set therefore
 * contains none of it (#163). `scripts/check-no-fixtures.ts` is what fails
 * the build if that ever stops being true.
 *
 * Keep the test a plain truthiness check on `import.meta.env.VITE_API_URL`,
 * written here, where the `import()` is. `lib/live.ts` says what a bundler
 * can and cannot see through; a test it cannot fold keeps the fixtures in.
 *
 * Synchronous for the API, a promise for fixtures — `DataSourceProvider`
 * holds on to whichever it gets.
 */
export function defaultDataSource(): DataSource | Promise<DataSource> {
  return import.meta.env.VITE_API_URL
    ? createApiDataSource()
    : import("@/adapters/fixture").then(({ createFixtureDataSource }) =>
        createFixtureDataSource(),
      );
}
